import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { LinkIcon } from '@heroicons/react/24/outline'
import { hubplatformAPI } from '../../services/api'
import { AUTH_ERROR, readAuthError } from '../../services/authErrors'
import { HUBPLATFORM_RESULT, readHubPlatformLanding } from '../../services/hubplatformLanding'
import { wasCancelled, withConfirmation } from './confirmCode'

const HUBPLATFORM_LINK_KEY = ['auth', 'hubplatform', 'link']

// The server sends naive UTC (no zone); read it as UTC.
const asUtc = (value) => new Date(/[zZ]|[+-]\d\d:\d\d$/.test(value) ? value : `${value}Z`)

const LANDING_MESSAGES = {
  [HUBPLATFORM_RESULT.LINKED]: ['success', 'hubplatform.card.linked'],
  [HUBPLATFORM_RESULT.TAKEN]: ['error', 'hubplatform.card.taken'],
  [HUBPLATFORM_RESULT.UNREACHABLE]: ['error', 'hubplatform.unreachable'],
  [HUBPLATFORM_RESULT.REFUSED]: ['error', 'hubplatform.card.linkFailed'],
}

/**
 * VH-317 — the HubPlatform section of the Security page (platform standard
 * §1.5): "Linked since <date>" and Unlink, or "Link HubPlatform". Linking is a
 * new way into the account, so it asks VitaHub's own code or passkey first
 * (`withConfirmation`). Hidden when the switch is off here.
 */
export default function HubPlatformCard() {
  const { t, i18n } = useTranslation()
  const location = useLocation()
  const queryClient = useQueryClient()
  const [asking, setAsking] = useState(false)
  const told = useRef(false)

  const query = useQuery({
    queryKey: HUBPLATFORM_LINK_KEY,
    queryFn: () => hubplatformAPI.link().then((response) => response.data),
  })

  // Back from HubPlatform: `#hubplatform=<result>`, said once, then gone.
  useEffect(() => {
    const landing = readHubPlatformLanding(location.hash)
    if (!landing || told.current) return
    told.current = true
    window.history.replaceState(null, '', location.pathname)
    const [kind, key] = LANDING_MESSAGES[landing.result] || LANDING_MESSAGES[HUBPLATFORM_RESULT.REFUSED]
    toast[kind](t(key))
    queryClient.invalidateQueries({ queryKey: HUBPLATFORM_LINK_KEY })
  }, [location.hash, location.pathname, queryClient, t])

  const linkMutation = useMutation({
    mutationFn: () => withConfirmation(() => hubplatformAPI.linkStart()),
    onSuccess: ({ data }) => window.location.assign(data.url),
    onError: (error) => {
      if (wasCancelled(error)) return
      const { kind } = readAuthError(error)
      toast.error(t(kind === AUTH_ERROR.NETWORK ? 'secondFactor.code.unreachable' : 'hubplatform.card.linkFailed'))
    },
  })

  const unlinkMutation = useMutation({
    mutationFn: () => hubplatformAPI.unlink(),
    onSuccess: () => toast.success(t('hubplatform.card.unlinked')),
    onError: () => toast.error(t('hubplatform.card.unlinkFailed')),
    onSettled: () => {
      setAsking(false)
      queryClient.invalidateQueries({ queryKey: HUBPLATFORM_LINK_KEY })
    },
  })

  if (!query.data?.enabled) return null
  const { linked, linked_since: since } = query.data
  const date = since
    ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(asUtc(since))
    : ''

  return (
    <section aria-labelledby="security-hubplatform" className="card">
      <div className="card-header">
        <div className="flex items-center">
          <LinkIcon className="mr-2 h-6 w-6 text-indigo-600" aria-hidden="true" />
          <h3 id="security-hubplatform" className="text-lg font-medium text-gray-900">
            {t('hubplatform.card.title')}
          </h3>
        </div>
      </div>
      <div className="card-body space-y-4">
        <p className="text-sm text-gray-700">{t('hubplatform.card.intro')}</p>
        {linked ? (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-green-700">{t('hubplatform.card.linkedSince', { date })}</p>
            {asking ? (
              <div className="flex flex-col gap-2 sm:flex-row">
                <button type="button" onClick={() => setAsking(false)} className="btn-secondary w-full sm:w-auto">
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  onClick={() => unlinkMutation.mutate()}
                  disabled={unlinkMutation.isPending}
                  className="btn-danger w-full sm:w-auto"
                >
                  {t('hubplatform.card.unlinkConfirm')}
                </button>
              </div>
            ) : (
              <button type="button" onClick={() => setAsking(true)} className="btn-secondary w-full sm:w-auto">
                {t('hubplatform.card.unlink')}
              </button>
            )}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => linkMutation.mutate()}
            disabled={linkMutation.isPending}
            className="btn-primary w-full sm:w-auto"
          >
            {t('hubplatform.card.link')}
          </button>
        )}
      </div>
    </section>
  )
}
